import { ComponentPropsWithoutRef, ReactElement, useState } from 'react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import ModalHeader from '../../components/modals/ModalHeader';
import { useTranslation } from 'react-i18next';
import { IonItem, IonLabel, IonList } from '@ionic/react';
import classnames from 'classnames';
import ProfileIcon from '../../assets/icons/ProfileIcon';
import { IonAlertCustomEvent, OverlayEventDetail } from '@ionic/core';
import LogoutIcon from '../../assets/icons/LogoutIcon';
import HelpAndFaqsIcon from '../../assets/icons/HelpAndFaqsIcon';
import PaymentMethodIcon from '../../assets/icons/PaymentMethodIcon';
import { Modals } from '../../exports/Enums';
import { IModal } from '../../router/ModalRouter';
import Alert, { AlertButtonRole } from '../../components/modals/Alert';
import useAuth from '../../../../hooks/global/useAuth';
import LanguageIcon from '../../assets/icons/LanguageIcon';

interface AccountItem {
  title: string;
  description?: string;
  type: AccountItemEnum;
  icon: ReactElement;
  props?: ComponentPropsWithoutRef<typeof IonItem>;
}

type AccountItemHandler = Record<AccountItemEnum, () => void>;

export enum AccountItemEnum {
  ManageAccount = 'manage-account',
  PaymentMethods = 'payment-methods',
  Language = 'language',
  HelpAndFAQs = 'help-and-faqs',
  Logout = 'logout',
}

const LogoutConfirmationModal = ({ isOpen, onDismiss }: { isOpen: boolean; onDismiss: () => void }) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Features:Profile:Components:ProfileModal:LogoutConfirmationModal',
  });

  const { logout } = useAuth();

  return (
    <Alert
      title={t('title')}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      buttons={[
        {
          text: t('buttons.logout'),
          role: AlertButtonRole.Destructive,
          handler: logout,
        },
        {
          text: t('buttons.cancel'),
          role: AlertButtonRole.Cancel,
        },
      ]}
    />
  );
};

const ProfileModal = ({ openModal, closeModal }: Partial<IModal>) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Features:Profile:Components:ProfileModal:ProfileModal',
  });

  const { isLoggedIn } = useAuth();
  const [isLogoutConfirmationOpen, setIsLogoutConfirmationOpen] = useState(false);

  const toggleLogoutConfirmation = (event?: IonAlertCustomEvent<OverlayEventDetail>): void => {
    setIsLogoutConfirmationOpen((prev) => !prev);

    if (event?.detail?.role === AlertButtonRole.Destructive) {
      closeModal();
    }
  };

  const items: AccountItem[] = [
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.ManageAccount}.title`),
      description: t(`items.${AccountItemEnum.ManageAccount}.description`),
      icon: <ProfileIcon className="stroke-black size-6" />,
      type: AccountItemEnum.ManageAccount,
    },
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.PaymentMethods}.title`),
      description: t(`items.${AccountItemEnum.PaymentMethods}.description`),
      icon: <PaymentMethodIcon className="stroke-black size-6" />,
      type: AccountItemEnum.PaymentMethods,
    },
    {
      title: t(`items.${AccountItemEnum.Language}.title`),
      icon: <LanguageIcon className="size-6" />,
      type: AccountItemEnum.Language,
    },
    {
      title: t(`items.${AccountItemEnum.HelpAndFAQs}.title`),
      icon: <HelpAndFaqsIcon className="stroke-black size-6" />,
      type: AccountItemEnum.HelpAndFAQs,
    },
    isLoggedIn && {
      title: t(`items.${AccountItemEnum.Logout}.title`),
      icon: <LogoutIcon className="stroke-black size-6" />,
      type: AccountItemEnum.Logout,
    },
  ].filter(Boolean);

  const ACCOUNT_ITEM_HANDLERS: AccountItemHandler = {
    [AccountItemEnum.ManageAccount]: () => openModal({ id: Modals.ManageAccount }),
    [AccountItemEnum.PaymentMethods]: () => openModal({ id: Modals.PaymentMethods }),
    [AccountItemEnum.Language]: () => openModal({ id: Modals.LanguageSwitcher }),
    [AccountItemEnum.HelpAndFAQs]: () => openModal({ id: Modals.HelpAndFAQs }),
    [AccountItemEnum.Logout]: toggleLogoutConfirmation,
  };

  return (
    <ModalWrapper>
      <ModalHeader title={t('title')} onClose={closeModal} />
      <IonList>
        <LogoutConfirmationModal isOpen={isLogoutConfirmationOpen} onDismiss={toggleLogoutConfirmation} />
        {items.map(({ props: { className, ...rest } = {}, title, description, icon, type }) => (
          <IonItem
            button
            className={classnames('[&::part(native)]:px-md space-x-md', className)}
            style={{
              '--inner-padding-end': 0,
            }}
            key={type}
            {...rest}
            onClick={ACCOUNT_ITEM_HANDLERS[type]}
          >
            {icon}
            <IonLabel className="pr-md">
              <h3 className="!font-semibold text-sm">{title}</h3>
              {description && <p className="!text-xs !text-muted-foreground font-medium">{description}</p>}
            </IonLabel>
          </IonItem>
        ))}
      </IonList>
    </ModalWrapper>
  );
};

export default ProfileModal;
